import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, RefreshCw, Send, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Loader from '../../components/ui/Loader';
import Badge from '../../components/ui/Badge';
import usuarioService from '../../services/usuarioService';
import usePermisos from '../../hooks/usePermisos';

export default function Usuarios() {
  const { tienePermiso } = usePermisos();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Estados para modales de confirmación
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, data: null });

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usuarioService.getAllUsuarios();
      setUsuarios(data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingItem) {
        await usuarioService.updateUsuario(editingItem.id, data);
        toast.success('Usuario actualizado');
      } else {
        await usuarioService.createUsuario(data);
        toast.success('Usuario creado');
      }
      loadUsuarios();
      closeModal();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    setConfirmModal({ show: true, type: 'delete', data: { id } });
  };

  const confirmDelete = async () => {
    try {
      await usuarioService.deleteUsuario(confirmModal.data.id);
      toast.success('Usuario eliminado');
      loadUsuarios();
    } catch (error) {
      toast.error('Error al eliminar');
      console.error(error);
    } finally {
      setConfirmModal({ show: false, type: null, data: null });
    }
  };

  const handleResendCredentials = async (usuario) => {
    if (!usuario.email) {
      toast.error('El usuario no tiene email registrado');
      return;
    }
    setConfirmModal({ show: true, type: 'resend', data: usuario });
  };

  const confirmResend = async () => {
    try {
      await usuarioService.resendCredentials(confirmModal.data.id);
      toast.success(`Credenciales enviadas a ${confirmModal.data.email}`);
    } catch (error) {
      toast.error('Error al enviar credenciales');
      console.error(error);
    } finally {
      setConfirmModal({ show: false, type: null, data: null });
    }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Filtrar usuarios: excluir POSTULANTE (se gestionan desde Procesos)
  const filteredUsuarios = usuarios
    .filter(u => u.rol !== 'POSTULANTE') // Solo mostrar SISTEMAS y SUPERVISOR
    .filter(u =>
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.dni?.includes(searchTerm)
    );

  const getRoleBadge = (rol) => {
    const roleColors = {
      SISTEMAS: 'danger',
      SUPERVISOR: 'warning'
    };
    return <Badge variant={roleColors[rol] || 'secondary'}>{rol}</Badge>;
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-neutral-800">Usuarios del Sistema</h1>
          <p className="text-neutral-600 mt-1">Gestión de administradores y supervisores</p>
        </div>
        {tienePermiso('crearUsuarios') && (
          <Button onClick={() => openModal()} className="gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Usuario
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>
        <Button variant="outline" onClick={loadUsuarios} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Actualizar</span>
        </Button>
      </div>

      {/* Tabla Desktop */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden hidden lg:block">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">DNI</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Email</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Nombre</th>
              <th className="text-left p-4 text-sm font-semibold text-neutral-700">Rol</th>
              <th className="text-right p-4 text-sm font-semibold text-neutral-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filteredUsuarios.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-8 text-center text-neutral-500">
                  No se encontraron usuarios
                </td>
              </tr>
            ) : (
              filteredUsuarios.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-neutral-50">
                  <td className="p-4 text-neutral-600">{usuario.dni}</td>
                  <td className="p-4 font-medium text-neutral-800">{usuario.email}</td>
                  <td className="p-4 text-neutral-600">{`${usuario.name || ''} ${usuario.lastname || ''}`.trim() || 'N/A'}</td>
                  <td className="p-4">{getRoleBadge(usuario.rol)}</td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      {tienePermiso('reenviarCredenciales') && (
                        <button
                          onClick={() => handleResendCredentials(usuario)}
                          className="p-2 text-[#297373] hover:bg-[#297373]/10 rounded-md transition-colors"
                          title="Reenviar credenciales"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      {tienePermiso('editarUsuarios') && (
                        <button
                          onClick={() => openModal(usuario)}
                          className="p-2 text-[#fe7f2d] hover:bg-[#fe7f2d]/10 rounded-md transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {tienePermiso('eliminarUsuarios') && (
                        <button
                          onClick={() => handleDelete(usuario.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cards Móvil/Tablet */}
      <div className="lg:hidden space-y-3">
        {filteredUsuarios.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8 text-center text-neutral-500">
            No se encontraron usuarios
          </div>
        ) : (
          filteredUsuarios.map((usuario) => (
            <div key={usuario.id} className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-neutral-800 truncate">
                    {`${usuario.name || ''} ${usuario.lastname || ''}`.trim() || 'N/A'}
                  </h3>
                  <p className="text-sm text-neutral-500 truncate">{usuario.email}</p>
                </div>
                {getRoleBadge(usuario.rol)}
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
                <span className="text-sm text-neutral-600">
                  <span className="font-medium">DNI:</span> {usuario.dni}
                </span>
                <div className="flex gap-1">
                  {tienePermiso('reenviarCredenciales') && (
                    <button
                      onClick={() => handleResendCredentials(usuario)}
                      className="p-2 text-[#297373] hover:bg-[#297373]/10 rounded-md transition-colors"
                      title="Reenviar credenciales"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  {tienePermiso('editarUsuarios') && (
                    <button
                      onClick={() => openModal(usuario)}
                      className="p-2 text-[#fe7f2d] hover:bg-[#fe7f2d]/10 rounded-md transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {tienePermiso('eliminarUsuarios') && (
                    <button
                      onClick={() => handleDelete(usuario.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <UsuarioModal
        isOpen={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingItem}
      />

      {/* Modal de Confirmación */}
      <Modal
        isOpen={confirmModal.show}
        onClose={() => setConfirmModal({ show: false, type: null, data: null })}
        title={confirmModal.type === 'delete' ? 'Confirmar eliminación' : 'Confirmar envío'}
      >
        <div className="space-y-4">
          {confirmModal.type === 'delete' ? (
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-neutral-700">
                  ¿Está seguro de eliminar este usuario?
                </p>
                <p className="text-sm text-neutral-500 mt-1">
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <div className="p-3 bg-[#297373]/10 rounded-full">
                <Mail className="w-6 h-6 text-[#297373]" />
              </div>
              <div>
                <p className="text-neutral-700">
                  ¿Enviar credenciales de acceso a:
                </p>
                <p className="font-semibold text-[#297373] mt-1">
                  {confirmModal.data?.email}
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setConfirmModal({ show: false, type: null, data: null })}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmModal.type === 'delete' ? confirmDelete : confirmResend}
              className={confirmModal.type === 'delete' 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-[#297373] hover:bg-[#297373]/90'
              }
            >
              {confirmModal.type === 'delete' ? 'Eliminar' : 'Enviar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function UsuarioModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState({
    name: '',
    lastname: '',
    email: '',
    password: '',
    dni: '',
    rol: 'POSTULANTE'
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        lastname: initialData.lastname || '',
        email: initialData.email || '',
        password: '', // No mostrar password
        dni: initialData.dni || '',
        rol: initialData.rol || 'POSTULANTE'
      });
    } else {
      setFormData({
        name: '',
        lastname: '',
        email: '',
        password: '',
        dni: '',
        rol: 'POSTULANTE'
      });
    }
  }, [initialData, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Si es edición y no se cambió la contraseña, no enviarla
    const dataToSend = { 
      ...formData,
      name: formData.name.toUpperCase(),
      lastname: formData.lastname.toUpperCase()
    };
    if (initialData && !dataToSend.password) {
      delete dataToSend.password;
    }
    onSave(dataToSend);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Editar Usuario' : 'Nuevo Usuario'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Nombre"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
            required
            placeholder="Nombre"
          />
          <Input
            label="Apellido"
            value={formData.lastname}
            onChange={(e) => setFormData({ ...formData, lastname: e.target.value.toUpperCase() })}
            required
            placeholder="Apellido"
          />
        </div>
        <Input
          label="DNI"
          value={formData.dni}
          onChange={(e) => setFormData({ ...formData, dni: e.target.value })}
          required
          maxLength={8}
        />
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          placeholder="correo@ejemplo.com"
        />
       
        <Input
          label={initialData ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña"}
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required={!initialData}
        />
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Rol
          </label>
          <select
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.rol}
            onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
            required
          >
            <option value="POSTULANTE">Postulante</option>
            <option value="RECLUTADOR">Reclutador</option>
            <option value="ANALISTA">Analista</option>
            <option value="JEFE_SELECCION">Jefe de Selección</option>
            <option value="SUPERVISOR">Supervisor</option>
            <option value="SISTEMAS">Sistemas</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
